/**
 * `document_move` — moved out of documentTree.js when workspaces arrived
 * (card 4.4) so the delete file stays about deletes.
 *
 * Move a node to another parent, optionally to a position within its new
 * sibling group. Not destructive: it deletes nothing, and its one dangerous
 * outcome — detaching a subtree by moving a node into its own descendant — is
 * refused outright rather than gated.
 *
 * ## `scope` is the TARGET
 *
 * The user's personal workspaces (`personal`, `personal/<id>`) share one
 * records bucket, so a move between them is the same one-row write as any
 * other move: the node is found anywhere in the bucket, and a root takes the
 * target workspace's `scopeId` (a nested node inherits its new root's).
 * `personal` here means the Personal workspace, not "wherever it was".
 *
 * Into or out of a TEAM scope stays refused: that is a data-residency change,
 * the documents analogue of `moveBoardToTeam`, and it is out at any grant
 * level. `moveNode` throws on one anyway; this answers with `invalid_parent`
 * ("exists, but not where the arguments claim") before anything is written,
 * so the model learns why rather than not_found.
 */
import {
  listTree,
  moveNode,
  keyBetween,
  keysExhausted,
} from '../../extensions/bundled/documents/documentsStore';
import { indexNodes, isDescendant, childrenOf } from '../../extensions/bundled/documents/documentsTree';
import { toDocScope, workspaceIdOf, resolveScopes, PERSONAL } from '../scopes';
import { refuseUnwritableDocScope, docScopeReport } from '../docScopes';
import { mcpError } from '../errors';

const isPersonalWire = (wire) => wire === PERSONAL || workspaceIdOf(wire) != null;

/** The workspace a personal target names: its id, or null for Personal. */
const targetWorkspace = (wire) => workspaceIdOf(wire) || null;

/** The bucket the target lives in: every personal workspace reads the whole personal bucket. */
const bucketWire = (wire) => (isPersonalWire(wire) ? PERSONAL : wire);

/** Is `nodeId` in a bucket other than the target's? Read only after a miss. */
async function livesElsewhere(wire, nodeId) {
  const here = bucketWire(wire);
  const others = [PERSONAL, ...resolveScopes(null).scopes].filter((w, i, all) => w !== here && all.indexOf(w) === i);
  for (const other of others) {
    // eslint-disable-next-line no-await-in-loop
    const tree = await listTree(toDocScope(other)).catch(() => []);
    if ((tree || []).some((n) => String(n.id) === nodeId)) return true;
  }
  return false;
}

export async function documentMove({ node_id: nodeId, new_parent_id: newParentId, scope, position }) {
  const wire = scope || PERSONAL;
  if (position !== undefined && (!Number.isInteger(position) || position < 0)) {
    return mcpError('invalid_argument', 'position must be a whole number, 0 or greater.', {
      field: 'position',
    });
  }
  const refusal = await refuseUnwritableDocScope(wire);
  if (refusal) return refusal;

  const personal = isPersonalWire(wire);
  const workspace = targetWorkspace(wire);
  const nodes = await listTree(toDocScope(bucketWire(wire)));
  const index = indexNodes(nodes);
  const node = index.nodeById.get(String(nodeId));
  if (!node) {
    if (await livesElsewhere(wire, String(nodeId))) {
      return mcpError(
        'invalid_parent',
        'That node is in another scope. Moves between the user\'s personal workspaces are allowed; into or out of a team they are not. Nothing was moved.',
        { nodeId: String(nodeId), scope: wire }
      );
    }
    return mcpError('not_found', 'No document or folder with that id in this scope.');
  }

  const parentId = newParentId == null ? null : String(newParentId);
  if (parentId !== null) {
    if (parentId === String(node.id)) {
      return mcpError('invalid_parent', 'A node cannot be moved into itself.', {
        nodeId: node.id,
        newParentId: parentId,
      });
    }
    const parent = index.nodeById.get(parentId);
    // A personal target's parent must be IN the target workspace: the node
    // follows its new root, so a parent elsewhere would land it somewhere the
    // caller did not name.
    if (!parent || (personal && (parent.scopeId || null) !== workspace)) {
      return mcpError('not_found', 'No document or folder with that id in this scope.', {
        newParentId: parentId,
      });
    }
    if (parent.type !== 'folder' && parent.type !== 'document') {
      return mcpError('invalid_parent', 'Only a folder or a document can hold other items.', {
        newParentId: parentId,
      });
    }
    // THE detach case: the whole subtree would leave the root and become
    // invisible in the sidebar while its rows keep syncing.
    if (isDescendant(index, parentId, String(node.id))) {
      return mcpError(
        'invalid_parent',
        'That target is inside the node you are moving, so the move would detach the whole subtree from the root. Nothing was moved.',
        { nodeId: node.id, newParentId: parentId }
      );
    }
  }

  // The sibling group it is joining, in stored order, without the node itself.
  // At the root of a personal target that is the target workspace's roots only.
  const siblings = childrenOf(index, parentId)
    .filter((n) => String(n.id) !== String(node.id))
    .filter((n) => parentId !== null || !personal || (n.scopeId || null) === workspace)
    .sort((a, b) => String(a.sortKey).localeCompare(String(b.sortKey)));
  const at = position === undefined ? siblings.length : Math.min(position, siblings.length);
  const prevKey = at > 0 ? siblings[at - 1]?.sortKey ?? null : null;
  const nextKey = at < siblings.length ? siblings[at]?.sortKey ?? null : null;
  // A gap with no integer left in it cannot take a new key. Minting a colliding
  // one would put two siblings in an order decided by their ids; the sidebar
  // resequences a whole group to fix this and MCP does not, so it refuses.
  if (keysExhausted(prevKey, nextKey)) {
    return mcpError(
      'invalid_argument',
      'There is no room between those two items. Move it to the end instead, or reorder that folder in the app.',
      { field: 'position' }
    );
  }
  const sortKey = keyBetween(prevKey, nextKey);

  // A personal target is `{ type: 'personal', scopeId }`: `movedData` stamps a
  // root with it (or clears it for Personal) and drops it from a nested node.
  const target = personal ? { type: 'personal', scopeId: workspace } : toDocScope(wire);
  await moveNode(node, parentId, sortKey, target);

  return {
    id: node.id,
    type: node.type,
    name: node.name,
    parentId,
    sortKey,
    position: at,
    scope: wire,
    ...docScopeReport(wire),
  };
}
